import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    getEventData,
    hideFixedNotificationsWrapper,
    addNotification,
    modalClosed,
    toggleNewRequestModal,
    confirmModalClosed,
    hideTrialBarEvent,
    setDashboardNewRequestModal,
} from '../../store/EventData/actions/eventData';
import { GlobalEventDistributor } from '../../../init/globalEventDistributor';
import {
    EVENTS_SUCCESS,
    EVENTS_SHOW_FOOTER,
    EVENTS_ERROR,
    EVENTS_WARNING,
    EVENTS_INFO,
    EVENTS_CLOSE_MODAL,
    HIDE_TRIAL_BAR,
    HIDE_FIXED_NOTIFICATIONS_WRAPPER,
    OPEN_NEW_REQUEST_MODAL,
    RESPONSE_CONFIRM_LEAVE_MODAL,
    EVENTS_NOTIFICATION_ERROR,
    OPEN_DASHBOARD_NEW_REQUEST_MODAL,
} from '../../constants/events';
import * as Sentry from '@sentry/browser';
import { getUrlParams } from '../../../helpers/urlHelper';
import { ALLOWED_SENTRY_MICROS, MICROS_SUBSCRIPTION_TAG, MICROS_TAG } from '../../../helpers/SentryUtils';
import { ModalCardButtonActions } from '../../constants/ModalCardButtonType';

type EventsReceiverProps = {
    globalEventDistributor: GlobalEventDistributor;
};

export const EventsReceiver = ({ globalEventDistributor }: EventsReceiverProps) => {
    const dispatch = useDispatch();
    useEffect(() => listenEvent(), []);

    const listenEvent: () => void = () => {
        globalEventDistributor.listenEvent(EVENTS_SUCCESS, notificationEventContent);
        globalEventDistributor.listenEvent(EVENTS_ERROR, handleErrorNotification);
        globalEventDistributor.listenEvent(EVENTS_NOTIFICATION_ERROR, handleErrorNotification);
        globalEventDistributor.listenEvent(EVENTS_WARNING, notificationEventContent);
        globalEventDistributor.listenEvent(EVENTS_INFO, notificationEventContent);
        globalEventDistributor.listenEvent(EVENTS_SHOW_FOOTER, getEvent);
        globalEventDistributor.listenEvent(EVENTS_CLOSE_MODAL, modalEventClosed);
        globalEventDistributor.listenEvent(OPEN_NEW_REQUEST_MODAL, toggleNewRequestModalEvent);
        globalEventDistributor.listenEvent(RESPONSE_CONFIRM_LEAVE_MODAL, confirmModalEventClosed);
        globalEventDistributor.listenEvent(HIDE_TRIAL_BAR, TrialBarHideEvent);
        globalEventDistributor.listenEvent(HIDE_FIXED_NOTIFICATIONS_WRAPPER, hideFixedNotificationsWrapperEvent);
        globalEventDistributor.listenEvent(OPEN_DASHBOARD_NEW_REQUEST_MODAL, openDashboardNewRequestModalEvent);
    };

    const getEvent: () => void = () => dispatch(getEventData());

    const notificationEventContent: (event: CustomEvent | ErrorEvent) => void = (event: CustomEvent) => {
        if (event.detail) {
            dispatch(addNotification(event));
        }
    };

    const handleErrorNotification: (event: ErrorEvent) => void = (event: ErrorEvent) => {
        const params = Array.from(getUrlParams());
        const isAllowedMicro = params.find((param: [string, string]) => ALLOWED_SENTRY_MICROS.indexOf(param[0]) > -1);

        if (event.type === EVENTS_ERROR && isAllowedMicro) {
            Sentry.setTag(MICROS_TAG, MICROS_SUBSCRIPTION_TAG);
            Sentry.captureException(event);
        }

        notificationEventContent(event);
    };

    const modalEventClosed: (event: CustomEvent) => void = (event: CustomEvent) => dispatch(modalClosed(event));

    const confirmModalEventClosed: (event: CustomEvent) => void = (event: CustomEvent) =>
        dispatch(confirmModalClosed(event));

    const toggleNewRequestModalEvent: (event: CustomEvent) => void = (event: CustomEvent) =>
        dispatch(toggleNewRequestModal(true));

    const openDashboardNewRequestModalEvent: (
        event: CustomEvent<{ content: { requestOrigin: keyof ModalCardButtonActions } }>
    ) => void = event => {
        dispatch(setDashboardNewRequestModal({ open: true, requestOrigin: event.detail.content.requestOrigin }));
    };

    const TrialBarHideEvent: (event: CustomEvent) => void = (event: CustomEvent) =>
        dispatch(hideTrialBarEvent(event.detail.content));

    const hideFixedNotificationsWrapperEvent: (event: CustomEvent) => void = (event: CustomEvent) =>
        dispatch(hideFixedNotificationsWrapper(event.detail.content));

    return null;
};
